import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  BarterForSection,
  CategorySection,
  DescriptionSection,
  ListingLocationSection,
  TitleSection,
} from "./FormSection";
import { ImageUploader } from "./ImageUploader";
import { SubmitButton } from "./SubmitButton";
import { useListingForm } from "./useListingForm";
import { createListing } from "../../api/listingApi";
import { useAuth } from "../../contexts/AuthContext";

export const CreateListingForm = () => {
  const {
    formData,
    handleChange,
    imageFile,
    imagePreview,
    handleImageChange,
    removeImage,
    errors,
    validateForm,
    resetForm,
    isSubmitting,
    setIsSubmitting,
  } = useListingForm();

  const navigate = useNavigate();
  const { currentUser, isAuthenticated } = useAuth();
  const [submitError, setSubmitError] = useState("");

  const profileLocation = currentUser?.location || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!isAuthenticated) {
      setSubmitError("You must be logged in to create a listing");
      return;
    }

    if (!validateForm(profileLocation)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description);

      const categoryId = formData.category._id || formData.category;
      data.append("category", categoryId);

      // Location: only send custom location if user toggled override.
      // Otherwise, the backend falls back to the seller's saved profile location.
      if (!formData.useProfileLocation && formData.location) {
        // Backend accepts location as a JSON string in multipart form data.
        // It parses it server-side. See controllers/listingController.js.
        data.append(
          "location",
          JSON.stringify({
            coordinates: formData.location.coordinates,
            precision: formData.location.precision || "geocoded",
          })
        );
      }
      // (when useProfileLocation is true, we omit `location` entirely — the
      // backend will inherit from the seller's profile)

      data.append("image", imageFile);

      // barterFor — array of category IDs
      formData.barterFor.forEach((item) => {
        const itemId = item._id || item;
        data.append("barterFor[]", itemId);
      });

      const result = await createListing(data);

      if (result.success) {
        resetForm();
        navigate("/browse", {
          state: { message: "Listing created successfully!" },
        });
      } else {
        setSubmitError(result.message || "Failed to create listing");
      }
    } catch (error) {
      console.error("Error creating listing:", error);
      setSubmitError("An error occurred while creating the listing");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 animate-fade-in">
      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>
      )}

      <TitleSection
        value={formData.title}
        onChange={handleChange("title")}
        error={errors.title}
      />

      <DescriptionSection
        value={formData.description}
        onChange={handleChange("description")}
        error={errors.description}
      />

      <CategorySection
        value={formData.category}
        onChange={handleChange("category")}
        error={errors.category}
      />

      <ListingLocationSection
        profileLocation={profileLocation}
        value={formData.location}
        onChange={handleChange("location")}
        useProfile={formData.useProfileLocation}
        onToggleProfile={(useIt) => {
          handleChange("useProfileLocation")(useIt);
          if (useIt) {
            // Clear any custom location when switching back to profile
            handleChange("location")(null);
          }
        }}
        error={errors.location}
      />

      <BarterForSection
        value={formData.barterFor}
        onChange={handleChange("barterFor")}
        error={errors.barterFor}
      />

      <ImageUploader
        imagePreview={imagePreview}
        onImageChange={handleImageChange}
        onRemoveImage={removeImage}
        error={errors.image}
      />

      <SubmitButton isSubmitting={isSubmitting} />
    </form>
  );
};